import { Field, Form, Formik } from "formik";
import useChatBot from "../hooks/useChatBot";
import remarkGfm from "remark-gfm";
import Markdown from "react-markdown";
import { FC, useEffect, useState } from "react";
import sendIcon from "../assets/images/icon/send-icon.png";
import { Message } from "../types/objectTypes";

interface ChatBotProps {
  chatBot: { isActive: boolean; key: string; type: string };
  onChatBotDisable: (key: string) => void;
}

const ChatBot: FC<ChatBotProps> = ({ chatBot, onChatBotDisable }) => {
  const { messages, send, isLoading, resumeSend } = useChatBot(chatBot.type);
  const [showMessages, setShowMessages] = useState(true);

  useEffect(() => {
    resumeSend(onChatBotDisable);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chatBot.isActive) {
      setShowMessages(false);
    } else {
      setShowMessages(true);
    }
  }, [chatBot]);

  useEffect(() => {
    document
      .getElementById(`bottom-${chatBot.type}`)
      ?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatBot]);

  return (
    <>
      {chatBot.key && (
        <button
          className="toggle-button"
          onClick={() => setShowMessages((prev) => !prev)}
        >
          Show earlier conversation
          <i
            className={`fa-solid ${
              showMessages ? "fa-angle-down" : "fa-angle-right"
            }`}
          ></i>
        </button>
      )}
      {showMessages ? (
        <div className="chatbot-messages">
          {messages.map((message: Message, index: number) => {
            const showThinking =
              isLoading &&
              ((message.role === "assistant" && !message.content) ||
                message.role === "error");

            return (
              <div key={index} className={`message ${message.role}`}>
                {showThinking ? (
                  <div className="thinking">
                    <i className="fa-solid fa-spinner fa-spin"></i> Thinking...
                  </div>
                ) : message.content ? (
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {message.content}
                  </Markdown>
                ) : null}
              </div>
            );
          })}

          <div id={`bottom-${chatBot.type}`} />
        </div>
      ) : (
        <div className="chatbot-messages">
          {messages.length > 0 && (
            <div className={`message ${messages[messages.length - 1].role}`}>
              {(() => {
                const lastMessage = messages[messages.length - 1];
                const isThinking =
                  isLoading &&
                  ((lastMessage.role === "assistant" && !lastMessage.content) ||
                    lastMessage.role === "error");

                if (isThinking) {
                  return (
                    <div className="thinking">
                      <i className="fa-solid fa-spinner fa-spin"></i>{" "}
                      Thinking...
                    </div>
                  );
                }

                return lastMessage.content ? (
                  <Markdown remarkPlugins={[remarkGfm]}>
                    {lastMessage.content}
                  </Markdown>
                ) : null;
              })()}
            </div>
          )}
        </div>
      )}
      <Formik
        initialValues={{ input: "", type: chatBot.type }}
        validate={(values) => {
          const errors: any = {};
          if (!values.input.trim()) {
            errors.input = "Message cannot be empty";
          }
          return errors;
        }}
        onSubmit={(values, { resetForm }) => {
          resetForm();
          send(values, (key: string) => {
            onChatBotDisable(key);
          });
        }}
      >
        {({ values }) => (
          <Form className="chatbot-form">
            {chatBot.isActive && (
              <div className="input-field">
                <Field
                  type="text"
                  name="input"
                  placeholder="Write a message"
                  className="chatbot-input"
                  disabled={
                    isLoading || messages[messages.length - 1]?.role === "error"
                  }
                />
                {values.input.trim() && (
                  <button
                    type="submit"
                    className="chatbot-submit"
                    disabled={
                      isLoading ||
                      messages[messages.length - 1]?.role === "error"
                    }
                  >
                    <img src={sendIcon} alt="Send Icon" />
                  </button>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ChatBot;
